import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, X, RotateCcw, RefreshCw, UserPlus, StickyNote, Zap, Mic, Square, Layers } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* Anwesenheits-Tafel (22.09.2026, seit 28.09.2026 als gemeinsamer Baustein):
   dieselbe Oberflaeche im CRM (/trainer, mit Login) und auf dem externen
   Trainer-Link (/trainer/<token>, ohne Login). Was sich unterscheidet, kommt
   ueber `api` herein:
     heute()                       -> Tagesplan
     status(buchungId, status)     -> bestaetigt/anwesend/gefehlt
     hinzufuegen(courseId, memberId)
     suche(text)                   -> [{id, name}]
     sprache(courseId, {audio, text}) -> Treffer (Reinsprechen)
     anwesendMehrere(courseId, ids)
     notiz(memberId, text)
     schnellerfassung(courseId, body)  optional (nur CRM: neue Person anlegen)
     mitgliedOeffnen(memberId)         optional (nur CRM: Lead-Karte) */

const STATUS = {
  bestaetigt: { label: 'bestätigt', cls: 'bg-muted text-muted-foreground border-border', next: 'anwesend' },
  anwesend: { label: 'anwesend', cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40', next: 'gefehlt' },
  gefehlt: { label: 'gefehlt', cls: 'bg-red-500/10 text-red-600 border-red-500/40', next: 'bestaetigt' },
};
const MAX_SPRACHE = 45;

export default function AnwesenheitTafel({ api, kopf = true, intervallMs = 60000, onLadeFehler }) {
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [aktiv, setAktiv] = useState(null);
  const [busy, setBusy] = useState(null);
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [notizFuer, setNotizFuer] = useState(null);
  const [notizText, setNotizText] = useState('');
  const [schnell, setSchnell] = useState(null);
  const [schnellBusy, setSchnellBusy] = useState(false);
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [sekunden, setSekunden] = useState(0);
  const [sprachBusy, setSprachBusy] = useState(false);
  const [sprachText, setSprachText] = useState('');
  const [sprache, setSprache] = useState(null);
  const [wahl, setWahl] = useState({});
  const recorder = useRef(null); const teile = useRef([]); const timer = useRef(null);

  const lade = useCallback(async () => {
    setLaden(true);
    try {
      const d = (await api.heute()).data;
      setDaten(d);
      setAktiv((a) => a || d.kurse.find((k) => !k.vorbei)?.course_id || d.kurse[0]?.course_id || null);
    } catch (e) {
      onLadeFehler?.(e);
      // abgelaufene Sitzung meldet die Seite selbst („Bitte neu anmelden") — kein Toast im Takt
      if (e?.response?.status !== 401) toast.error(e?.response?.data?.detail || 'Tagesplan konnte nicht geladen werden');
    }
    finally { setLaden(false); }
  }, [api, onLadeFehler]);
  useEffect(() => { lade(); }, [lade]);
  // Standard 60 s; das Trainer-Tablet (Stations-Rolle) laedt alle 30 s von selbst neu
  useEffect(() => { const t = setInterval(lade, intervallMs); return () => clearInterval(t); }, [lade, intervallMs]);

  const kurs = useMemo(() => daten?.kurse.find((k) => k.course_id === aktiv), [daten, aktiv]);

  const tap = async (t) => {
    const ziel = STATUS[t.status].next;
    setBusy(t.buchung_id);
    try { await api.status(t.buchung_id, ziel); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Status nicht gesetzt'); }
    finally { setBusy(null); }
  };

  useEffect(() => {
    if (!suche.trim() || suche.trim().length < 2) { setTreffer([]); return; }
    const h = setTimeout(async () => {
      try { setTreffer(await api.suche(suche.trim())); } catch { setTreffer([]); }
    }, 250);
    return () => clearTimeout(h);
  }, [suche, api]);

  const hinzufuegen = async (m) => {
    if (!kurs) return;
    try { await api.hinzufuegen(kurs.course_id, m.id); toast.success(`${m.name} eingetragen`); setSuche(''); setTreffer([]); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Konnte nicht eintragen'); }
  };

  const schnellOeffnen = () => setSchnell({ art: 'probetraining', name: '', phone: '', email: '', kurswunsch: kurs?.name || '' });
  const schnellSpeichern = async () => {
    if (!schnell || !kurs || !api.schnellerfassung) return;
    if (!schnell.name.trim()) { toast.error('Name fehlt'); return; }
    if (!schnell.phone.trim() && !schnell.email.trim()) { toast.error('Telefon oder E-Mail angeben'); return; }
    setSchnellBusy(true);
    try {
      const r = (await api.schnellerfassung(kurs.course_id, {
        art: schnell.art, name: schnell.name.trim(), phone: schnell.phone.trim() || null,
        email: schnell.email.trim() || null, kurswunsch: schnell.kurswunsch.trim() || null,
      })).data;
      toast.success(r.neu ? `${schnell.name.trim()} angelegt und eingetragen` : `${schnell.name.trim()} war schon bekannt — eingetragen`);
      setSchnell(null); lade();
    } catch (e) { toast.error(e.response?.data?.detail || 'Schnellerfassung fehlgeschlagen'); }
    finally { setSchnellBusy(false); }
  };

  /* Reinsprechen (28.09.2026): Namen -> Whisper -> Treffer zum Abhaken; nichts
     wird automatisch gesetzt, damit ein Hoerfehler niemanden falsch eincheckt. */
  const spracheAuswerten = async ({ audio, text }) => {
    if (!kurs) return;
    setSprachBusy(true);
    try {
      const r = (await api.sprache(kurs.course_id, { audio, text })).data;
      setSprache(r);
      setWahl(Object.fromEntries(r.ergebnisse.map(e => [e.gesprochen, e.sicher && e.treffer ? e.treffer.member_id : ''])));
      setSprachText('');
      if (!r.ergebnisse.length) toast.warning(`Keine Namen verstanden: „${r.transkript}“`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nichts verstanden'); }
    finally { setSprachBusy(false); }
  };
  const aufnahmeStopp = () => { try { recorder.current?.state !== 'inactive' && recorder.current?.stop(); } catch { /* egal */ } };
  useEffect(() => () => { clearInterval(timer.current); aufnahmeStopp(); }, []);
  const aufnahmeStart = async () => {
    if (!kurs) { toast.error('Erst einen Kurs wählen'); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme in diesem Browser nicht möglich — Namen unten tippen'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find(t => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = () => {
        clearInterval(timer.current); setNimmtAuf(false);
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(teile.current, { type: rec.mimeType || 'audio/webm' });
        const endung = (rec.mimeType || '').includes('mp4') ? 'm4a' : (rec.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
        const f = new File([blob], `anwesenheit.${endung}`, { type: blob.type });
        if (f.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        spracheAuswerten({ audio: f });
      };
      recorder.current = rec; rec.start(1000);
      setNimmtAuf(true); setSekunden(0);
      timer.current = setInterval(() => setSekunden(s => { if (s + 1 >= MAX_SPRACHE) aufnahmeStopp(); return s + 1; }), 1000);
    } catch { toast.error('Mikrofon nicht freigegeben'); }
  };
  const spracheSetzen = async () => {
    const ids = [...new Set(Object.values(wahl).filter(Boolean))];
    if (!ids.length || !kurs) return;
    setSprachBusy(true);
    try {
      const r = (await api.anwesendMehrere(kurs.course_id, ids)).data;
      const n = r.anwesend.length + r.hinzugefuegt.length;
      toast.success(`${n} anwesend gesetzt${r.hinzugefuegt.length ? ` (${r.hinzugefuegt.length} ohne Buchung eingetragen)` : ''}${r.fehler.length ? ` · ${r.fehler.length} nicht möglich` : ''}`);
      setSprache(null); setWahl({}); lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Anwesend setzen fehlgeschlagen'); }
    finally { setSprachBusy(false); }
  };

  const notizSpeichern = async () => {
    if (!notizFuer || !notizText.trim()) return;
    try { await api.notiz(notizFuer.member_id, notizText.trim()); toast.success('Notiz gespeichert'); setNotizFuer(null); setNotizText(''); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Notiz nicht gespeichert'); }
  };

  return (
    <div className="space-y-4" data-testid="anwesenheit-tafel">
      {kopf && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-muted-foreground text-sm">{daten ? new Date(daten.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' }) : '…'} · Antippen: bestätigt → anwesend → gefehlt</p>
          <Button variant="outline" size="sm" onClick={lade} disabled={laden} className="gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${laden ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="trainer-kurse">
        {(daten?.kurse || []).map((k) => (
          <button key={k.course_id} type="button" onClick={() => setAktiv(k.course_id)} data-testid={`kurs-${k.course_id}`}
            className={`flex-shrink-0 rounded-lg border px-3 py-2 text-left min-w-[150px] transition-colors ${aktiv === k.course_id ? 'border-primary bg-primary/10' : 'border-border/60 hover:bg-accent'} ${k.vorbei ? 'opacity-60' : ''}`}>
            <p className="text-xs text-muted-foreground">{k.start}–{k.end}</p>
            <p className="font-medium text-sm truncate">{k.name}{k.ausfall ? ' · fällt aus' : ''}</p>
            <p className="text-[11px] text-muted-foreground">{k.anwesend}/{k.gebucht} da{k.gefehlt ? ` · ${k.gefehlt} gefehlt` : ''}</p>
          </button>
        ))}
        {daten && daten.kurse.length === 0 && <p className="text-sm text-muted-foreground">Heute keine Kurse.</p>}
      </div>

      {kurs && (
        <>
          {/* Trainingsplaner A5: Zyklus-Kopf, falls fuer diesen Kurs ein freigegebener Zyklus laeuft */}
          {kurs.zyklus && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap text-sm" data-testid="zyklus-kopf">
              <Layers className="w-4 h-4 text-primary" />
              <span className="font-semibold">Woche {kurs.zyklus.woche}/{kurs.zyklus.wochen_gesamt}</span>
              <span className="text-muted-foreground">· {kurs.zyklus.phase}</span>
              {kurs.zyklus.typ_label && <Badge variant="outline" className="text-[10px]">{kurs.zyklus.typ_label}</Badge>}
              {kurs.zyklus.lehrplan_thema && <span className="text-muted-foreground">· {kurs.zyklus.lehrplan_thema}</span>}
              <span className="text-xs text-muted-foreground ml-auto truncate">{kurs.zyklus.name}</span>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="trainer-teilnehmer">
            {kurs.teilnehmer.map((t) => { const S = STATUS[t.status]; return (
              <div key={t.buchung_id} className={`rounded-lg border-2 p-3 flex items-center gap-3 ${S.cls}`} data-testid={`tn-${t.buchung_id}`}>
                <button type="button" onClick={() => tap(t)} disabled={busy === t.buchung_id} className="flex-1 min-w-0 text-left" aria-label={`${t.name}: ${S.label}`}>
                  <p className="font-semibold text-base truncate">{t.name}</p>
                  <p className="text-[11px] flex items-center gap-1.5 flex-wrap">
                    <span>{S.label}</span>
                    {t.minderjaehrig && <Badge variant="outline" className="text-[9px] px-1 py-0">Kind{t.abgeholt ? ' · abgeholt' : ''}</Badge>}
                    {t.keine_fotos && <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-400 text-red-600" data-testid={`keine-fotos-${t.buchung_id}`}>Keine Fotos</Badge>}
                    {t.letzte_notiz && <span className="opacity-70 truncate">· {t.letzte_notiz.text}</span>}
                  </p>
                </button>
                <div className="flex flex-col gap-1">
                  <span className="w-8 h-8 rounded-full flex items-center justify-center bg-background/70">
                    {t.status === 'anwesend' ? <Check className="w-4 h-4" /> : t.status === 'gefehlt' ? <X className="w-4 h-4" /> : <RotateCcw className="w-3.5 h-3.5 opacity-40" />}
                  </span>
                  <button type="button" className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background/70" title="Trainingsnotiz" onClick={() => { setNotizFuer(t); setNotizText(''); }}>
                    <StickyNote className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ); })}
            {kurs.teilnehmer.length === 0 && <p className="text-sm text-muted-foreground col-span-full">Noch niemand gebucht — unten nachtragen.</p>}
          </div>

          {/* Reinsprechen */}
          <Card className="border-border/60" data-testid="trainer-sprache">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                {!nimmtAuf
                  ? <Button className="h-11 gap-2 flex-1 min-w-[200px]" onClick={aufnahmeStart} disabled={sprachBusy} data-testid="sprache-start"><Mic className="w-4 h-4" /> Reinsprechen: wer ist da?</Button>
                  : <Button className="h-11 gap-2 flex-1 min-w-[200px] bg-red-600 hover:bg-red-700" onClick={aufnahmeStopp} data-testid="sprache-stopp"><Square className="w-4 h-4" /> Fertig ({sekunden}s)</Button>}
                <Input value={sprachText} onChange={(e) => setSprachText(e.target.value)} placeholder="oder tippen: Max, Lisa Berger, Tom" className="h-11 flex-[2] min-w-[200px]"
                  onKeyDown={(e) => { if (e.key === 'Enter' && sprachText.trim()) spracheAuswerten({ text: sprachText.trim() }); }} data-testid="sprache-text" />
              </div>
              <p className="text-xs text-muted-foreground">Namen nacheinander sagen, Vorname reicht, wenn er im Kurs eindeutig ist. Danach prüfen und mit einem Tipp alle anwesend setzen.{sprachBusy ? ' Wird ausgewertet…' : ''}</p>
              {sprache && (
                <div className="space-y-2 pt-1" data-testid="sprache-ergebnis">
                  <p className="text-xs text-muted-foreground">Verstanden: „{sprache.transkript}“</p>
                  {sprache.ergebnisse.map((e) => {
                    const optionen = [e.treffer, ...(e.alternativen || [])].filter(Boolean);
                    const gewaehlt = wahl[e.gesprochen] || '';
                    return (
                      <div key={e.gesprochen} className={`rounded-lg border px-3 py-2 flex items-center gap-3 flex-wrap ${gewaehlt ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}>
                        <input type="checkbox" className="w-5 h-5 accent-emerald-600" checked={!!gewaehlt} disabled={!optionen.length}
                          onChange={(ev) => setWahl(w => ({ ...w, [e.gesprochen]: ev.target.checked ? (gewaehlt || optionen[0]?.member_id || '') : '' }))} />
                        <span className="font-medium min-w-[90px]">„{e.gesprochen}“</span>
                        {optionen.length === 0 ? <span className="text-xs text-muted-foreground">kein Treffer — unten suchen</span>
                          : optionen.length === 1 && e.sicher ? <span className="text-sm">→ {optionen[0].name}{optionen[0].quelle === 'mitglied' ? ' (ohne Buchung)' : optionen[0].status === 'anwesend' ? ' (schon da)' : ''}</span>
                          : <select value={gewaehlt} onChange={(ev) => setWahl(w => ({ ...w, [e.gesprochen]: ev.target.value }))} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                              <option value="">– wer ist gemeint? –</option>
                              {optionen.map(o => <option key={o.member_id} value={o.member_id}>{o.name}{o.quelle === 'mitglied' ? ' (ohne Buchung)' : ''}</option>)}
                            </select>}
                      </div>
                    );
                  })}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setSprache(null); setWahl({}); }}>Verwerfen</Button>
                    <Button size="sm" className="gap-1.5" onClick={spracheSetzen} disabled={sprachBusy || !Object.values(wahl).some(Boolean)} data-testid="sprache-setzen"><Check className="w-4 h-4" /> {Object.values(wahl).filter(Boolean).length} anwesend setzen</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-muted-foreground" />
                <Input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder={api.schnellerfassung ? 'Mitglied ohne Buchung eintragen — Name oder Telefon' : 'Mitglied ohne Buchung eintragen — Name'} className="h-10" data-testid="trainer-suche" />
              </div>
              {treffer.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {treffer.filter((m) => !kurs.teilnehmer.some((t) => t.member_id === m.id)).map((m) => (
                    <Button key={m.id} size="sm" variant="outline" className="h-9" onClick={() => hinzufuegen(m)}>{m.name}</Button>
                  ))}
                </div>
              )}
              {api.schnellerfassung && (
                <Button variant="secondary" className="h-10 w-full gap-2" onClick={schnellOeffnen} data-testid="trainer-schnellerfassung">
                  <Zap className="w-4 h-4" /> Neu am Eingang: Probetraining / Mitglied erfassen
                </Button>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!schnell} onOpenChange={(o) => { if (!o) setSchnell(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Schnellerfassung · {kurs?.name}</DialogTitle></DialogHeader>
          {schnell && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[['probetraining', 'Probetraining'], ['mitglied', 'Will Mitglied werden']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setSchnell({ ...schnell, art: k })} data-testid={`schnell-art-${k}`}
                    className={`rounded-lg border-2 px-3 py-3 text-sm font-medium ${schnell.art === k ? 'border-primary bg-primary/10' : 'border-border/60'}`}>{l}</button>
                ))}
              </div>
              <Input value={schnell.name} onChange={(e) => setSchnell({ ...schnell, name: e.target.value })} placeholder="Name *" className="h-11 text-base" autoFocus data-testid="schnell-name" />
              <Input value={schnell.phone} onChange={(e) => setSchnell({ ...schnell, phone: e.target.value })} placeholder="Telefonnummer" type="tel" inputMode="tel" className="h-11 text-base" data-testid="schnell-phone" />
              <Input value={schnell.email} onChange={(e) => setSchnell({ ...schnell, email: e.target.value })} placeholder="E-Mail" type="email" inputMode="email" className="h-11 text-base" data-testid="schnell-email" />
              <div className="space-y-1">
                <Input value={schnell.kurswunsch} onChange={(e) => setSchnell({ ...schnell, kurswunsch: e.target.value })} placeholder="Kurswunsch" className="h-11 text-base" list="schnell-kurse" data-testid="schnell-kurswunsch" />
                <datalist id="schnell-kurse">{[...new Set((daten?.kurse || []).map((k) => k.name))].map((n) => <option key={n} value={n} />)}</datalist>
                <div className="flex flex-wrap gap-1.5">
                  {[...new Set((daten?.kurse || []).map((k) => k.name))].slice(0, 6).map((n) => (
                    <button key={n} type="button" onClick={() => setSchnell({ ...schnell, kurswunsch: n })}
                      className={`rounded-full border px-2.5 py-1 text-xs ${schnell.kurswunsch === n ? 'border-primary bg-primary/10' : 'border-border/60'}`}>{n}</button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {schnell.art === 'probetraining'
                  ? 'Wird als Probetraining (besucht) angelegt, heute als anwesend eingetragen; morgen steht „nachfassen“ auf /heute.'
                  : 'Wird als Lead angelegt und heute eingetragen; das Büro bekommt die Aufgabe „Vertrag abschließen“ (Verkaufsrechner).'}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSchnell(null)}>Abbrechen</Button>
                <Button onClick={schnellSpeichern} disabled={schnellBusy} data-testid="schnell-speichern">{schnellBusy ? '…' : 'Anlegen & eintragen'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!notizFuer} onOpenChange={(o) => { if (!o) setNotizFuer(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Trainingsnotiz · {notizFuer?.name}</DialogTitle></DialogHeader>
          <Input value={notizText} onChange={(e) => setNotizText(e.target.value)} placeholder="z. B. Kombination 3 sitzt, Deckung hoch halten" autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') notizSpeichern(); }} data-testid="notiz-text" />
          <div className="flex justify-between items-center gap-2">
            {api.mitgliedOeffnen ? <button type="button" className="text-xs text-primary hover:underline" onClick={() => api.mitgliedOeffnen(notizFuer?.member_id)}>Mitglied öffnen</button> : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setNotizFuer(null)}>Abbrechen</Button>
              <Button onClick={notizSpeichern} disabled={!notizText.trim()} data-testid="notiz-speichern">Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
